import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import api from "../../lib/api";
import { Card, Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { formatPrice } from "../../lib/utils";

const EMPTY_FORM = {
  name: "",
  description: "",
  price: "",
  billing_period_days: 30,
  service_ids: [],
};

export default function Memberships() {
  const [memberships, setMemberships] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = () =>
    api
      .get("/owner/memberships")
      .then(({ data }) => setMemberships(data))
      .catch(() => setMemberships([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    api.get("/owner/services").then(({ data }) => setServices(data)).catch(() => setServices([]));
  }, []);

  const startCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const startEdit = (membership) => {
    setEditingId(membership.id);
    setForm({
      name: membership.name,
      description: membership.description || "",
      price: membership.price,
      billing_period_days: membership.billing_period_days,
      service_ids: membership.services?.map((s) => s.id) || [],
    });
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const toggleService = (id) => {
    setForm((f) => ({
      ...f,
      service_ids: f.service_ids.includes(id) ? f.service_ids.filter((s) => s !== id) : [...f.service_ids, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/owner/memberships/${editingId}`, form);
      } else {
        await api.post("/owner/memberships", form);
      }
      cancelForm();
      load();
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id) => {
    await api.delete(`/owner/memberships/${id}`);
    load();
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-montserrat text-2xl font-bold text-slate-900">Memberships</h1>
        <Button size="sm" onClick={() => (showForm ? cancelForm() : startCreate())}>
          <Plus className="h-4 w-4" /> Add membership
        </Button>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        Offer unlimited visits to selected services for a recurring price — turns occasional clients into
        regulars.
      </p>

      {showForm && (
        <Card className="mb-6 p-4">
          <p className="mb-3 text-sm font-semibold text-slate-900">{editingId ? "Edit membership" : "New membership"}</p>
          <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input placeholder="Membership name (e.g. Unlimited Monthly)" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input placeholder="Price per period" type="number" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            <Input placeholder="Billing period (days)" type="number" value={form.billing_period_days} onChange={(e) => setForm({ ...form, billing_period_days: e.target.value })} />
            <Input
              placeholder="Description (optional)"
              className="sm:col-span-2"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />

            <div className="sm:col-span-2">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Included services</p>
              <div className="flex flex-wrap gap-2">
                {services.map((s) => (
                  <button
                    type="button"
                    key={s.id}
                    onClick={() => toggleService(s.id)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
                      form.service_ids.includes(s.id) ? "border-primary bg-primary/5 text-primary" : "border-border text-slate-600"
                    }`}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" disabled={saving || form.service_ids.length === 0}>
                {saving ? "Saving…" : editingId ? "Save changes" : "Save membership"}
              </Button>
              <Button type="button" variant="outline" onClick={cancelForm}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : memberships.length === 0 ? (
        <p className="text-muted-foreground">No memberships yet — create one above.</p>
      ) : (
        <div className="divide-y divide-border rounded-2xl border border-border">
          {memberships.map((m) => (
            <div key={m.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div>
                <p className="font-medium text-slate-900">{m.name}</p>
                <p className="text-xs text-muted-foreground">
                  {m.services?.map((s) => s.name).join(", ")} · every {m.billing_period_days} days
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-semibold text-slate-900">{formatPrice(m.price)}</span>
                <Badge variant={m.is_active ? "accent" : "default"}>{m.is_active ? "Active" : "Inactive"}</Badge>
                <button onClick={() => startEdit(m)} className="text-muted-foreground hover:text-primary">
                  <Pencil className="h-4 w-4" />
                </button>
                {m.is_active && (
                  <button onClick={() => deactivate(m.id)} className="text-muted-foreground hover:text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
